/**
 * ProfilePageModern.js
 * Page de profil unifiée pour tous les espaces (membre, cooper, expert, admin, super_admin).
 * Design glassmorphism, UI/UX premium.
 */
import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Save, Mail, Building2, Award, Camera, Edit3, Clock, FileText, Lock, HardHat, Loader2, Shield, Activity } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useProfilePageModern } from './useProfilePageModern';
import ProfileTabsContent from './ProfileTabsContent';

export default function ProfilePageModern({ userType = 'member' }) {
  const {
    profile, loading, saving, activeTab, setActiveTab, editMode, setEditMode,
    uploadingPhoto, fileInputRef, formData, phoneData, setPhoneData,
    handleChange, handlePhotoUpload, handleSubmit, initFormData,
    selectedIndicatif, phoneValidation, roleConfig, getAuthHeader,
  } = useProfilePageModern({ userType });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-white/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-t-white rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-white/60">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  const RoleIcon = roleConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Header avec effet glassmorphism */}
      <div className={`relative overflow-hidden bg-gradient-to-r ${roleConfig.color} pb-32`}>
        {/* Patterns décoratifs */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 pt-8">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <BackButton variant="dark" label="Retour" />

            <div className="flex items-center gap-3">
              {!editMode ? (
                <Button
                  onClick={() => setEditMode(true)}
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  variant="outline"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => { setEditMode(false); initFormData(); }}
                    className="text-white/80 hover:text-white hover:bg-white/10"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="bg-white text-slate-900 hover:bg-white/90"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Enregistrer
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Profile Header */}
          <div className="flex flex-col md:flex-row items-center md:items-end gap-6 text-white">
            {/* Photo de profil */}
            <div className="relative group">
              <div className="w-32 h-32 rounded-2xl bg-white/20 backdrop-blur-xl border-4 border-white/30 overflow-hidden shadow-2xl">
                {formData.photo_profil_url ? (
                  <img
                    src={formData.photo_profil_url}
                    alt="Photo de profil"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <RoleIcon className="h-16 w-16 text-white/60" />
                  </div>
                )}
              </div>
              {editMode && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  ) : (
                    <Camera className="h-8 w-8 text-white" />
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>

            {/* Infos principales */}
            <div className="text-center md:text-left flex-1">
              <Badge className={`${roleConfig.badge} mb-2`}>
                <RoleIcon className="h-3 w-3 mr-1" />
                {roleConfig.label}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold">
                {formData.prenom} {formData.nom}
              </h1>
              {formData.raison_sociale && (
                <p className="text-xl text-white/80 mt-1">{formData.raison_sociale}</p>
              )}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-3 text-white/70">
                {formData.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {formData.email}
                  </span>
                )}
                {profile?.numero_adherent && (
                  <span className="flex items-center gap-1 font-mono bg-white/20 px-2 py-0.5 rounded">
                    #{profile.numero_adherent}
                  </span>
                )}
                {profile?.ref_ess && (
                  <span className="flex items-center gap-1 font-mono bg-white/20 px-2 py-0.5 rounded">
                    ESS: {profile.ref_ess}
                  </span>
                )}
              </div>
            </div>

            {/* Stats rapides */}
            <div className="hidden lg:flex gap-4">
              {profile?.date_inscription && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center min-w-[100px]">
                  <Clock className="h-5 w-5 mx-auto mb-1 text-white/60" />
                  <p className="text-xs text-white/60">Membre depuis</p>
                  <p className="font-semibold">{new Date(profile.date_inscription).getFullYear()}</p>
                </div>
              )}
              {profile?.statut && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center min-w-[100px]">
                  <Activity className="h-5 w-5 mx-auto mb-1 text-white/60" />
                  <p className="text-xs text-white/60">Statut</p>
                  <p className="font-semibold capitalize">{profile.statut}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal avec tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-20 relative z-10 pb-12">
        <Card className="shadow-2xl border-0 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b bg-slate-50">
              <TabsList className="w-full justify-start bg-transparent h-auto p-0 gap-0">
                <TabsTrigger
                  value="informations"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-6 py-4"
                >
                  <User className="h-4 w-4 mr-2" />
                  Informations
                </TabsTrigger>
                {(profile?.role === 'beneficiaire_pro' || profile?.role === 'cooper' || userType === 'cooper') && (
                  <TabsTrigger
                    value="entreprise"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-6 py-4"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Entreprise
                  </TabsTrigger>
                )}
                {(profile?.role === 'beneficiaire_pro' || profile?.statut_juridique) && (
                  <TabsTrigger
                    value="activite"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-6 py-4"
                  >
                    <HardHat className="h-4 w-4 mr-2" />
                    Activité Pro
                  </TabsTrigger>
                )}
                {userType === 'expert' && (
                  <TabsTrigger
                    value="expertise"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-6 py-4"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Expertise
                  </TabsTrigger>
                )}
                {userType === 'cooper' && (
                  <TabsTrigger
                    value="mes-documents"
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 px-6 py-4"
                    data-testid="profile-tab-cooper-documents"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Mes documents
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="securite"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 px-6 py-4"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Sécurité
                </TabsTrigger>
                <TabsTrigger
                  value="mes-donnees"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 px-6 py-4"
                  data-testid="profile-tab-mes-donnees"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Mes données
                </TabsTrigger>
              </TabsList>
            </div>

            <CardContent className="p-6">
              <ProfileTabsContent
                profile={profile}
                userType={userType}
                editMode={editMode}
                formData={formData}
                handleChange={handleChange}
                phoneData={phoneData}
                setPhoneData={setPhoneData}
                selectedIndicatif={selectedIndicatif}
                phoneValidation={phoneValidation}
                getAuthHeader={getAuthHeader}
              />
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
